import { useEffect, useRef, useState } from "react";
import { Database, Combine, MapPin, Gauge, Spline, RefreshCw } from "lucide-react";

const VB_W = 1200;
const VB_H = 500;
/* Pulled apart a little to keep clearance between the bigger chips. */
const NODE_Y_HIGH = 190;
const NODE_Y_LOW = 310;

/**
 * The six stages, in the same plain language and the same order as the Process
 * page. Deliberately not the internal names (Aggregation / Harmonization /
 * Spatial Indexing / Interpolation): this is the first thing a visitor reads
 * about the pipeline, and it is not a white paper.
 *
 * "Locate" is a service area, not a hexagon — hexes are a drawing device, the
 * water system is the unit that gets scored.
 */
const STEPS = [
  {
    label: "Collect",
    icon: Database,
    title: "Collect the results",
    desc: "Gathers every public test result for your water system — federal programmes, state labs, and your utility's own report.",
  },
  {
    label: "Line up",
    icon: Combine,
    title: "Line them up",
    desc: "Puts every result in the same units, on the same timeline. A “not detected” result never becomes a number.",
  },
  {
    label: "Locate",
    icon: MapPin,
    title: "Find your system",
    desc: "Works out which water system actually serves your address — its real service area, not a zip code.",
  },
  {
    label: "Score",
    icon: Gauge,
    title: "Score what was measured",
    desc: "Compares each real result to the legal limit for it. Limits come from a dated public record, never from us.",
  },
  {
    label: "Estimate",
    icon: Spline,
    title: "Estimate the gaps",
    desc: "Fills the obvious gaps where nobody has tested — and labels every estimate as one.",
  },
  {
    label: "Refresh",
    icon: RefreshCw,
    title: "Keep it current",
    desc: "Re-runs everything whenever an agency publishes new data. Old scores are kept, not overwritten.",
  },
];

const NODE_FIRST_X = 220;
const NODE_LAST_X = 1020;
const NODE_SPACING = (NODE_LAST_X - NODE_FIRST_X) / (STEPS.length - 1);
const NODE_X = STEPS.map((_, i) => NODE_FIRST_X + i * NODE_SPACING);
const NODE_Y = STEPS.map((_, i) => (i % 2 === 0 ? NODE_Y_HIGH : NODE_Y_LOW));

/**
 * The timeline, in one place.
 *
 * Four beats inside about four seconds: the mesh draws itself left to right,
 * the sources tether themselves to Collect, the chips land one after another,
 * and then everything on the left keeps breathing forever.
 *
 * The beats deliberately overlap a little. A strictly sequential version
 * reads as three separate animations waiting for each other; a short overlap
 * reads as one thing happening.
 */
const MESH_SWEEP_MS = 1500;
const SOURCES_AT_MS = 1350;
const SOURCES_DRAW_MS = 700;
const CHIPS_AT_MS = 2150;
const STEP_INTERVAL_MS = 240;
const LOOP_AT_MS = CHIPS_AT_MS + STEP_INTERVAL_MS * STEPS.length + 200;

const CYCLE_PERIOD_S = 24;

/**
 * Occasionally one dot in the disorderly half lights up in the accent blue —
 * the same #2563EB the links hover to — and fades back out. It reads as a
 * reading arriving somewhere in the mess, which is what the left half is.
 *
 * Drawn by a small pool of overlay circles rather than by recolouring the dot
 * itself. Recolouring means crossfading a dot's own fill against the accent
 * mid-flight, and at the bottom of that fade the dot either vanishes or jumps;
 * an overlay just fades in over it and leaves the dot alone.
 */
const ACCENT = "#2563EB";
const PULSE_POOL = 5;
const PULSE_GAP_MS = 420;
const PULSE_IN_MS = 180;
const PULSE_OUT_MS = 560;

/**
 * The field of dots.
 *
 * One grid across the whole width, warped on the left and true on the right.
 * `CHAOS_END` is the fraction of the width at which the warp reaches zero —
 * half, so the left half is the disorderly part that moves and the right half
 * is the orderly part that does not. Because the warp eases to zero at that
 * boundary, so does the jitter, and the mesh does not tear where the two
 * halves meet.
 *
 * 30 x 13 rather than something denser: every dot in the left half is
 * repositioned on every frame, and the connecting lines are rebuilt from
 * those positions, so the count is a frame budget rather than a taste
 * decision.
 */
const GRID_COLS = 30;
const GRID_ROWS = 13;
const GRID_X_STEP = VB_W / GRID_COLS;
const GRID_Y_STEP = VB_H / GRID_ROWS;
const CHAOS_END = 0.5;
const MAX_WARP = 15;

function chaosFactor(xProgress) {
  if (xProgress >= CHAOS_END) return 0;
  const t = 1 - xProgress / CHAOS_END;
  return t * t * (3 - 2 * t); // smoothstep
}

function makeSeededRng(seed) {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

const gridRng = makeSeededRng(31337);

/**
 * Every dot carries its own drift parameters so the left half breathes out of
 * phase with itself rather than pulsing as one block. Amplitude scales with
 * the same chaos factor as the warp, which is what keeps the boundary still.
 */
const GRID = [];
for (let r = 0; r < GRID_ROWS; r++) {
  for (let c = 0; c < GRID_COLS; c++) {
    const baseX = (c + 0.5) * GRID_X_STEP;
    const baseY = (r + 0.5) * GRID_Y_STEP;
    const chaos = chaosFactor(baseX / VB_W);
    GRID.push({
      col: c,
      row: r,
      chaos,
      x: baseX + (gridRng() - 0.5) * 2 * MAX_WARP * chaos,
      y: baseY + (gridRng() - 0.5) * 2 * MAX_WARP * chaos,
      // Drift: amplitude, angular speed and phase, per axis.
      ax: (2.5 + gridRng() * 5) * chaos,
      ay: (2.5 + gridRng() * 5) * chaos,
      sx: 0.35 + gridRng() * 0.5,
      sy: 0.35 + gridRng() * 0.5,
      px: gridRng() * Math.PI * 2,
      py: gridRng() * Math.PI * 2,
      // Radius pulse, so the disorderly half reads as alive rather than
      // merely sliding around.
      r0: 0.9 + chaos * 0.8,
      ar: chaos * 0.45,
      sr: 0.8 + gridRng() * 0.9,
      pr: gridRng() * Math.PI * 2,
    });
  }
}

/** Dots that actually move — everything with a non-zero chaos factor. */
const MOVING = GRID.reduce(
  (acc, d, i) => (d.chaos > 0.001 ? (acc.push(i), acc) : acc),
  [],
);

/** Rows always contain moving points; only the left columns do. */
const MOVING_COLS = Array.from({ length: GRID_COLS }, (_, c) => c).filter(
  (c) => chaosFactor(((c + 0.5) * GRID_X_STEP) / VB_W) > 0.001,
);

/**
 * The named sources, each pinned to a real dot in the disorderly half.
 *
 * Coordinates are a hand-tuned layout that gives each acronym horizontal
 * clearance, then snapped to the nearest grid dot — so a source is attached
 * to a specific erratic dot and inherits its drift, rather than floating on
 * its own timeline beside it.
 */
/**
 * A ladder down the far left, alternating x so adjacent acronyms do not sit
 * directly above one another.
 *
 * Everything stays at x <= 95 for one reason: the Collect tooltip spans
 * roughly x 110-330 by y 40-160 in viewBox units, and a source inside that
 * box is drawn underneath an opaque card. USGS was at x 140 and disappeared
 * entirely behind it.
 */
const DATA_SOURCES = [
  { label: "EPA", x: 55, y: 60 },
  { label: "USGS", x: 95, y: 120 },
  { label: "WQP", x: 55, y: 180 },
  { label: "SDWIS", x: 95, y: 240 },
  { label: "NOAA", x: 55, y: 300 },
  { label: "UCMR5", x: 95, y: 360 },
  { label: "DEP", x: 55, y: 420 },
];

/**
 * Snapping, with two exclusions.
 *
 * A dot already claimed by another source is skipped, or two acronyms would
 * sit on the same point and render on top of each other. And anything inside
 * `COLLECT_KEEP_OUT` of the first chip is skipped: a label there is drawn
 * underneath the disc, which is where the old layout put UCMR5.
 */
const COLLECT_KEEP_OUT = 115;

const SOURCES = (() => {
  const taken = new Set();
  return DATA_SOURCES.map((src) => {
    let best = -1;
    let bestD = Infinity;
    GRID.forEach((d, i) => {
      if (d.chaos <= 0.001 || taken.has(i)) return;
      const fromChip = Math.hypot(d.x - NODE_X[0], d.y - NODE_Y[0]);
      if (fromChip < COLLECT_KEEP_OUT) return;
      const dist = (d.x - src.x) ** 2 + (d.y - src.y) ** 2;
      if (dist < bestD) {
        bestD = dist;
        best = i;
      }
    });
    taken.add(best);
    return { label: src.label, gridIndex: best };
  });
})();

const SOURCE_SET = new Set(SOURCES.map((s) => s.gridIndex));

function curveBetween(x1, y1, x2, y2) {
  const midX = (x1 + x2) / 2;
  return `M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`;
}

function ProcessFlow() {
  const containerRef = useRef(null);
  const leaveTimerRef = useRef(null);
  const dotRefs = useRef([]);
  const rowRefs = useRef([]);
  const colRefs = useRef([]);
  const tetherRefs = useRef([]);
  const labelRefs = useRef([]);
  const pulseRefs = useRef([]);
  const rafRef = useRef(0);

  const [triggered, setTriggered] = useState(false);
  const [phase, setPhase] = useState(0); // 0 idle · 1 mesh · 2 sources · 3 chips · 4 loop
  const [step, setStep] = useState(-1);
  const [revealComplete, setRevealComplete] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [hoveredIndex, setHoveredIndex] = useState(-1);

  // Hover takes precedence over the auto-pulse cycle. A short leave delay
  // bridges the small gap between the node disc and the tooltip above so
  // the user can move the cursor between them without flicker.
  const handleEnter = (i) => {
    if (!revealComplete) return;
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    setHoveredIndex(i);
  };
  const handleLeave = () => {
    if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    leaveTimerRef.current = setTimeout(() => {
      setHoveredIndex(-1);
      leaveTimerRef.current = null;
    }, 150);
  };
  useEffect(() => {
    return () => {
      if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    };
  }, []);

  /**
   * Nothing starts until the illustration is actually on screen. Fires once:
   * an animation that restarts every time it scrolls back into view is a
   * distraction rather than an introduction.
   */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setTriggered(true);
      },
      { threshold: 0.25 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  /** The beats. Reduced motion skips straight to the finished state. */
  useEffect(() => {
    if (!triggered) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setPhase(4);
      setStep(STEPS.length - 1);
      setRevealComplete(true);
      return;
    }

    const timers = [
      setTimeout(() => setPhase(1), 20),
      setTimeout(() => setPhase(2), SOURCES_AT_MS),
      setTimeout(() => setPhase(3), CHIPS_AT_MS),
      setTimeout(() => {
        setPhase(4);
        setRevealComplete(true);
      }, LOOP_AT_MS),
    ];
    return () => timers.forEach(clearTimeout);
  }, [triggered]);

  /**
   * The chips land one at a time once their beat arrives.
   *
   * Depends on the boolean, not on `phase`. Depending on `phase` meant this
   * effect re-ran when the timeline advanced from 3 to 4, which cleared the
   * interval and restarted the cascade from the first chip — so the sequence
   * played twice and the last edge was still drawing itself five seconds in.
   */
  const chipsRunning = phase >= 3;
  useEffect(() => {
    if (!chipsRunning) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    setStep(0);
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setStep(i);
      if (i >= STEPS.length - 1) clearInterval(id);
    }, STEP_INTERVAL_MS);
    return () => clearInterval(id);
  }, [chipsRunning]);

  /**
   * The drift, and the only part of this that runs every frame.
   *
   * Written straight to the DOM rather than through state on purpose. The
   * left half is 225 dots plus the lines threaded through them; putting that
   * in React state would mean 225 reconciliations per frame to move some
   * circles, which is the wrong tool. The positions are computed once per
   * frame into a flat array and the affected attributes are set directly.
   *
   * The lines have to be rebuilt from the same array in the same frame. A
   * mesh whose dots move while its lines stay put does not read as a mesh.
   */
  useEffect(() => {
    if (!triggered) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const live = new Float32Array(GRID.length * 2);
    GRID.forEach((d, i) => {
      live[i * 2] = d.x;
      live[i * 2 + 1] = d.y;
    });

    /* Which dot each pool slot is currently lighting, and when it started. */
    const pulses = Array.from({ length: PULSE_POOL }, () => ({ idx: -1, start: 0 }));
    let nextPulseAt = 0;
    const pulseRng = makeSeededRng(90210);

    const frame = (now) => {
      const t = now / 1000;

      for (const i of MOVING) {
        const d = GRID[i];
        const x = d.x + Math.sin(t * d.sx + d.px) * d.ax;
        const y = d.y + Math.cos(t * d.sy + d.py) * d.ay;
        live[i * 2] = x;
        live[i * 2 + 1] = y;
        const el = dotRefs.current[i];
        if (el) {
          el.setAttribute("cx", x);
          el.setAttribute("cy", y);
          el.setAttribute("r", d.r0 + Math.sin(t * d.sr + d.pr) * d.ar);
        }
      }

      for (let r = 0; r < GRID_ROWS; r++) {
        const el = rowRefs.current[r];
        if (!el) continue;
        let pts = "";
        for (let c = 0; c < GRID_COLS; c++) {
          const i = r * GRID_COLS + c;
          pts += `${live[i * 2]},${live[i * 2 + 1]} `;
        }
        el.setAttribute("points", pts);
      }

      for (const c of MOVING_COLS) {
        const el = colRefs.current[c];
        if (!el) continue;
        let pts = "";
        for (let r = 0; r < GRID_ROWS; r++) {
          const i = r * GRID_COLS + c;
          pts += `${live[i * 2]},${live[i * 2 + 1]} `;
        }
        el.setAttribute("points", pts);
      }

      // Sources ride their dot, so the tether and the label follow it.
      SOURCES.forEach((src, k) => {
        const x = live[src.gridIndex * 2];
        const y = live[src.gridIndex * 2 + 1];
        const line = tetherRefs.current[k];
        if (line) {
          line.setAttribute("x1", x);
          line.setAttribute("y1", y);
        }
        const label = labelRefs.current[k];
        if (label) {
          label.setAttribute("x", x);
          label.setAttribute("y", y - 9);
        }
      });

      /* Accent pulses. A free slot picks a random moving dot, fades up over
         PULSE_IN_MS and back down over PULSE_OUT_MS, then frees itself. */
      if (now >= nextPulseAt) {
        const slot = pulses.find((s2) => s2.idx === -1);
        if (slot) {
          slot.idx = MOVING[Math.floor(pulseRng() * MOVING.length)];
          slot.start = now;
        }
        nextPulseAt = now + PULSE_GAP_MS * (0.55 + pulseRng() * 0.9);
      }

      pulses.forEach((pulse, k) => {
        const el = pulseRefs.current[k];
        if (!el) return;
        if (pulse.idx === -1) {
          el.setAttribute("opacity", "0");
          return;
        }
        const age = now - pulse.start;
        if (age > PULSE_IN_MS + PULSE_OUT_MS) {
          pulse.idx = -1;
          el.setAttribute("opacity", "0");
          return;
        }
        const k2 =
          age < PULSE_IN_MS ? age / PULSE_IN_MS : 1 - (age - PULSE_IN_MS) / PULSE_OUT_MS;
        el.setAttribute("cx", live[pulse.idx * 2]);
        el.setAttribute("cy", live[pulse.idx * 2 + 1]);
        el.setAttribute("r", 1.6 + k2 * 2.6);
        el.setAttribute("opacity", (k2 * 0.95).toFixed(3));
      });

      rafRef.current = requestAnimationFrame(frame);
    };

    rafRef.current = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(rafRef.current);
  }, [triggered]);

  useEffect(() => {
    if (!revealComplete) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const perNodeMs = (CYCLE_PERIOD_S * 1000) / STEPS.length;
    const start = performance.now();
    const tick = () => {
      const idx = Math.floor((performance.now() - start) / perNodeMs) % STEPS.length;
      setActiveIndex(idx);
    };
    tick();
    const id = setInterval(tick, 100);
    return () => clearInterval(id);
  }, [revealComplete]);

  const displayedActive = hoveredIndex >= 0 ? hoveredIndex : activeIndex;

  const interEdges = NODE_X.slice(0, -1).map((x, i) => ({
    d: curveBetween(x, NODE_Y[i], NODE_X[i + 1], NODE_Y[i + 1]),
    revealAt: i + 1,
  }));

  return (
    <>
      {/* Desktop / tablet diagram */}
      <div
        ref={containerRef}
        className="relative w-full aspect-[12/5] mx-auto hidden md:block"
        aria-label="Chaos to clarity: collect, line up, locate, score, estimate, refresh"
      >
        <svg
          className="absolute inset-0 w-full h-full overflow-visible"
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          <defs>
            <radialGradient id="pf-bgGlow" cx="50%" cy="50%" r="40%">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.08" />
              <stop offset="70%" stopColor="#3b82f6" stopOpacity="0.02" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
            </radialGradient>

            {/*
              The draw-on, as one animated element rather than several hundred.

              Per-dot transition delays would mean ~450 elements each running
              their own transition to do a single sweep. A mask whose rect
              scales open from the left reveals the mesh in the same order for
              one composited transform, and the dots underneath never re-render.
            */}
            <mask id="pf-sweep" maskUnits="userSpaceOnUse">
              <rect
                width={VB_W}
                height={VB_H}
                fill="#fff"
                style={{
                  transformOrigin: "0 0",
                  transform: `scaleX(${phase >= 1 ? 1 : 0})`,
                  transition: `transform ${MESH_SWEEP_MS}ms cubic-bezier(0.35, 0, 0.65, 1)`,
                }}
              />
            </mask>
          </defs>

          <rect width={VB_W} height={VB_H} fill="url(#pf-bgGlow)" />

          {/* The field: warped and breathing on the left, true and still on
              the right. Lines behind dots so the dots read as the joints. */}
          <g mask="url(#pf-sweep)" className="pointer-events-none" aria-hidden="true">
            <g className="text-zp-ink/25">
              {Array.from({ length: GRID_ROWS }, (_, r) => (
                <polyline
                  key={`row-${r}`}
                  ref={(el) => (rowRefs.current[r] = el)}
                  points={Array.from(
                    { length: GRID_COLS },
                    (_, c) => `${GRID[r * GRID_COLS + c].x},${GRID[r * GRID_COLS + c].y}`,
                  ).join(" ")}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="0.4"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              ))}
              {Array.from({ length: GRID_COLS }, (_, c) => (
                <polyline
                  key={`col-${c}`}
                  ref={(el) => (colRefs.current[c] = el)}
                  points={Array.from(
                    { length: GRID_ROWS },
                    (_, r) => `${GRID[r * GRID_COLS + c].x},${GRID[r * GRID_COLS + c].y}`,
                  ).join(" ")}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="0.4"
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                />
              ))}
            </g>

            {GRID.map((d, i) => (
              <circle
                key={`dot-${i}`}
                ref={(el) => (dotRefs.current[i] = el)}
                cx={d.x}
                cy={d.y}
                r={d.r0}
                fill="currentColor"
                className={
                  SOURCE_SET.has(i)
                    ? "text-zp-ink"
                    : d.chaos > 0.001
                      ? "text-zp-ink/50"
                      : "text-zp-ink"
                }
              />
            ))}
            {/* Accent pulses, over the dots they light. */}
            {Array.from({ length: PULSE_POOL }, (_, k) => (
              <circle
                key={`pulse-${k}`}
                ref={(el) => (pulseRefs.current[k] = el)}
                cx={0}
                cy={0}
                r={0}
                opacity={0}
                fill={ACCENT}
              />
            ))}
          </g>

          {/* Sources: each tethered from its own erratic dot to Collect. The
              line draws itself on once, then rides the dot forever. */}
          <g aria-hidden="true">
            {SOURCES.map((src, k) => (
              <line
                key={`tether-${src.label}`}
                ref={(el) => (tetherRefs.current[k] = el)}
                x1={GRID[src.gridIndex].x}
                y1={GRID[src.gridIndex].y}
                x2={NODE_X[0]}
                y2={NODE_Y[0]}
                stroke="currentColor"
                strokeWidth="0.7"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
                className="text-zp-ink/85"
                style={{
                  strokeDasharray: 600,
                  strokeDashoffset: phase >= 2 ? 0 : 600,
                  opacity: phase >= 2 ? 1 : 0,
                  transition: `stroke-dashoffset ${SOURCES_DRAW_MS}ms ease-out, opacity 300ms ease-out`,
                  transitionDelay: `${k * 55}ms`,
                }}
              />
            ))}
            {SOURCES.map((src, k) => (
              <text
                key={`label-${src.label}`}
                ref={(el) => (labelRefs.current[k] = el)}
                x={GRID[src.gridIndex].x}
                y={GRID[src.gridIndex].y - 9}
                fontSize="10"
                fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                fill="currentColor"
                textAnchor="middle"
                letterSpacing="0.8"
                className="text-zp-ink/70 select-none pointer-events-none"
                style={{
                  opacity: phase >= 2 ? 1 : 0,
                  transition: "opacity 400ms ease-out",
                  transitionDelay: `${k * 55}ms`,
                }}
              >
                {src.label}
              </text>
            ))}
          </g>

          {/* Inter-node edges (curved through the stagger) */}
          {interEdges.map((ep, i) => (
            <path
              key={i}
              d={ep.d}
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
              fill="none"
              pathLength="1"
              className="text-zp-ink/85 transition-[stroke-dashoffset,opacity] duration-500 ease-out"
              style={{
                strokeDasharray: 1,
                strokeDashoffset: step >= ep.revealAt ? 0 : 1,
                opacity: step >= ep.revealAt ? 1 : 0,
                transitionDelay: step >= ep.revealAt ? "100ms" : "0ms",
              }}
            />
          ))}
        </svg>

        {/* Node tiles (HTML overlay) */}
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const xPct = (NODE_X[i] / VB_W) * 100;
          const yPct = (NODE_Y[i] / VB_H) * 100;
          const isActive = displayedActive === i && revealComplete;
          return (
            <div
              key={s.label}
              style={{ left: `${xPct}%`, top: `${yPct}%` }}
              className={`absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center transition-all duration-500 ease-out motion-reduce:transition-none ${
                step >= i ? "opacity-100 scale-100" : "opacity-0 scale-90"
              } ${revealComplete ? "cursor-pointer" : ""}`}
              onMouseEnter={() => handleEnter(i)}
              onMouseLeave={handleLeave}
              onFocus={() => handleEnter(i)}
              onBlur={handleLeave}
              tabIndex={revealComplete ? 0 : -1}
              aria-label={`${s.title}: ${s.desc}`}
            >
              {/* Half again as large, with the label inside under the icon.
                  The label's type is smaller than it was outside: it now has
                  to fit the disc's inner width rather than the space between
                  chips, and "REFRESH" is the constraint. */}
              <div
                className={`flex flex-col items-center justify-center gap-1 w-24 h-24 lg:w-[7.5rem] lg:h-[7.5rem] rounded-full border bg-white/95 backdrop-blur-sm transition-all duration-300 ${
                  isActive
                    ? "border-zp-ink shadow-[0_0_0_6px_rgba(15,17,20,0.10),0_14px_40px_rgba(15,17,20,0.20)] scale-110"
                    : "border-zp-ink/15 shadow-[0_6px_22px_rgba(15,23,42,0.07)] dark:shadow-[0_6px_26px_rgba(0,0,0,0.45)]"
                }`}
              >
                <Icon
                  className={`h-6 w-6 lg:h-7 lg:w-7 transition-colors duration-300 ${
                    isActive ? "text-zp-ink" : "text-zp-ink/70"
                  }`}
                />
                <span
                  className={`font-mono text-[9px] lg:text-[10px] font-medium uppercase tracking-[0.08em] whitespace-nowrap transition-colors duration-300 ${
                    isActive ? "text-zp-ink" : "text-zp-slate"
                  }`}
                >
                  {s.label}
                </span>
              </div>
            </div>
          );
        })}

        {/* Tooltips — always anchored above the node so they never overlap its label */}
        {STEPS.map((s, i) => {
          const isActive = displayedActive === i && revealComplete;
          /* Clears the disc, which is now ~100 viewBox units across rather
             than ~66, so the old 65 would have overlapped it. */
          const tooltipCenterY = NODE_Y[i] - 92;
          const xPct = (NODE_X[i] / VB_W) * 100;
          const yPct = (tooltipCenterY / VB_H) * 100;
          return (
            <div
              key={`tip-${s.label}`}
              style={{ left: `${xPct}%`, top: `${yPct}%` }}
              className={`absolute -translate-x-1/2 -translate-y-3/4 w-[220px] rounded-lg bg-white/95 backdrop-blur-sm border border-zp-ink/20 shadow-[0_10px_30px_rgba(15,23,42,0.12)] px-3.5 py-2.5 transition-all duration-300 ease-out motion-reduce:transition-none ${
                isActive
                  ? "opacity-100 scale-100"
                  : "opacity-0 scale-95 pointer-events-none"
              }`}
              role="tooltip"
              aria-hidden={!isActive}
              onMouseEnter={() => handleEnter(i)}
              onMouseLeave={handleLeave}
            >
              <div className="font-mono text-[12px] uppercase tracking-wider text-zp-ink mb-0.5">
                {/* {String(i + 1).padStart(2, "0")}.  */}
                {s.title}
              </div>
              <div className="text-[11px] text-zp-slate leading-snug">{s.desc}</div>
              {/* Arrow pointing down at the node below */}
              <div
                className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 rotate-45 bg-white border-r border-b border-zp-ink/20"
                aria-hidden="true"
              />
            </div>
          );
        })}
      </div>

      {/* Mobile fallback */}
      <div className="md:hidden grid grid-cols-2 gap-3">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <div
              key={s.label}
              className="flex items-center gap-2.5 rounded-xl border border-zp-ink/15 bg-white/90 backdrop-blur-sm px-3 py-2.5"
            >
              <span className="flex items-center justify-center w-9 h-9 rounded-full bg-zp-ink/[0.07] text-zp-ink shrink-0">
                <Icon className="h-4 w-4" />
              </span>
              <div className="leading-tight">
                <div className="font-mono text-[9px] text-zp-ink mb-0.5">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="text-[12px] font-medium uppercase tracking-wider text-zp-ink">
                  {s.label}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

export default ProcessFlow;
